import {
  type PendingRevitOperationRecoveryMetadata,
} from '../../../api/grpc/operationProgressClient';
import { buildRequestedParameterIntents } from '../../../api/grpc/requestedParameterIntent';
import {
  buildTolerantProjectParameterMutationBatches,
} from '../../revit/projectParameterMutationPlan';
import type { RevitFamily } from '../../../api/revit/types';
import { fetchDefinitionOnlyFamilySnapshot } from '../../revit/definitionOnlyFamilySnapshot';
import { fetchAvailableProjectParameters } from './availability';
import { rememberAvailableProjectParameters } from './availableProjectParameterCache';
import {
  areConnectorAdditionOperationsAvailable,
  buildConnectorAdditionOperations,
  buildConnectorFamilyIdentityKey,
  collectConnectorFamilyMutationTargetIds,
  selectConnectorFamilyParameterTargets,
  withConnectorAdditionTarget,
} from './connectorPlan';
import {
  type ConnectorFamilyTargetDisposition,
  resolveConnectorFamilyTargetDisposition,
} from './connectorFamilyTargetPolicy';
import { recordConnectorLiveEvidenceFamilySelection } from '../../../config/connectorLiveEvidence';
import { buildScopedBindingCompletionNotification, notifyParameterAddition } from './feedback';
import { describeConnectorExecutionMode, projectChangedResult } from './operationIdentity';
import {
  type ConnectorAdditionOperation,
  type ParameterAdditionOptions,
  type ParameterAdditionResult,
  type ParameterCompletionNotification,
} from './models';
import { type ConnectorExecutionContext } from './pendingConnectorRecovery';
import { type ParameterAdditionCore } from './useParameterAdditionCore';
import { type ParameterMutationActions } from './useParameterMutationActions';
import {
  createConnectorUndoCheckpoint,
  type ConnectorUndoCheckpoint,
} from './connectorUndoCheckpoint';

/**
 * Seams the connector snapshot plan needs from the hosting hook. Every value
 * the plan reads after an await comes from the frozen execution context or
 * from these injected collaborators — never re-read from live store state.
 */
export interface ConnectorAdditionPlanDeps {
  menuConfig: ParameterAdditionCore['menuConfig'];
  connectorConfig: ParameterAdditionCore['connectorConfig'];
  connectorKnownFamilyKeysRef: ParameterAdditionCore['connectorKnownFamilyKeysRef'];
  didProjectChange: ParameterAdditionCore['didProjectChange'];
  setIsAdding: ParameterAdditionCore['setIsAdding'];
  setConnectorParameterUndoPlan: ParameterAdditionCore['setConnectorParameterUndoPlan'];
  startParameterProgress: ParameterAdditionCore['startParameterProgress'];
  updateParameterProgressFromHost: ParameterAdditionCore['updateParameterProgressFromHost'];
  showError: ParameterAdditionCore['showError'];
  showInfo: ParameterAdditionCore['showInfo'];
  showSuccess: ParameterAdditionCore['showSuccess'];
  showWarning: ParameterAdditionCore['showWarning'];
  addAsProjectParameters: ParameterMutationActions['addAsProjectParameters'];
  addToConfiguredFamilyCategories: ParameterMutationActions['addToConfiguredFamilyCategories'];
}

interface ConnectorPlanNotifiers {
  options: ParameterAdditionOptions;
  showInfo: ParameterAdditionCore['showInfo'];
  showSuccess: ParameterAdditionCore['showSuccess'];
  showWarning: ParameterAdditionCore['showWarning'];
}

type AvailableProjectParameterDescriptors = Awaited<
  ReturnType<typeof fetchAvailableProjectParameters>
>;

type ConnectorFamilySelection = ReturnType<typeof selectConnectorFamilyParameterTargets>;

/**
 * The family selection plus the raw snapshot facts it was derived from. The
 * disposition is decided later, in a different function, so the counts that
 * explain it must travel with the selection instead of being re-read.
 */
interface ConnectorFamilySnapshotSelection {
  selection: ConnectorFamilySelection;
  snapshotAvailable: boolean;
  snapshotFamilyCount: number;
  snapshotCategories: string[];
}

const projectChangeAbort = (
  requestedProjectKey: string,
  didProjectChange: ConnectorAdditionPlanDeps['didProjectChange'],
  notifiers: ConnectorPlanNotifiers,
): ParameterAdditionResult | null => {
  if (!didProjectChange(requestedProjectKey)) {
    return null;
  }

  const changedResult = projectChangedResult();
  notifyParameterAddition(
    notifiers.options,
    notifiers.showWarning,
    changedResult.error ?? 'The active Revit project changed.',
  );
  return changedResult;
};

const countRequestedParameters = (operations: ConnectorAdditionOperation[]): number =>
  operations.reduce((sum, operation) => sum + operation.parameterNames.length, 0);

const buildAlreadyAvailableResult = (
  operations: ConnectorAdditionOperation[],
): ParameterAdditionResult => ({
  success: true,
  addedCount: 0,
  existingCount: countRequestedParameters(operations),
  queued: false,
  requestedCount: countRequestedParameters(operations),
  undoAvailable: false,
});

const collectSnapshotCategories = (families: RevitFamily[]): string[] =>
  Array.from(
    new Set(
      families
        .map((family) => (typeof family.category === 'string' ? family.category.trim() : ''))
        .filter(Boolean),
    ),
  ).sort();

/**
 * Publishes why the family half of a Family mode ran or was skipped. A
 * `nonApplicable` disposition ends the family operation without any gRPC
 * call, so without this record the live route evidence cannot tell an
 * intentional skip apart from a lost dispatch.
 */
const recordConnectorFamilyRouteEvidence = (args: {
  operation: ConnectorAdditionOperation;
  familySnapshot: ConnectorFamilySnapshotSelection;
  mutationTargetFamilyIds: string[];
  disposition: ConnectorFamilyTargetDisposition;
}): void => {
  recordConnectorLiveEvidenceFamilySelection({
    bindingKind: args.operation.bindingKind,
    requestedCategories: [...args.operation.categories],
    snapshotAvailable: args.familySnapshot.snapshotAvailable,
    snapshotFamilyCount: args.familySnapshot.snapshotFamilyCount,
    snapshotCategories: args.familySnapshot.snapshotCategories,
    matchingFamilyCount: args.familySnapshot.selection.matchingFamilyCount,
    compatibleFamilyCount: args.familySnapshot.selection.compatibleFamilyCount,
    mutationTargetFamilyCount: args.mutationTargetFamilyIds.length,
    disposition: args.disposition,
  });
};

// Resolve category membership before the first project write. Family-scope
// targeting plans from authoritative family-document definitions: the broad
// swept snapshot conflates project bindings with family parameters, so a
// swept row can mark a family satisfied that never received the parameter
// and the write would skip it.
const resolveFamilySelections = async (args: {
  operations: ConnectorAdditionOperation[];
  requestedProjectKey: string;
  deps: ConnectorAdditionPlanDeps;
  notifiers: ConnectorPlanNotifiers;
}): Promise<
  | { kind: 'ok'; selections: Map<ConnectorAdditionOperation, ConnectorFamilySnapshotSelection> }
  | { kind: 'abort'; result: ParameterAdditionResult }
> => {
  const selections = new Map<ConnectorAdditionOperation, ConnectorFamilySnapshotSelection>();
  for (const operation of args.operations) {
    if (operation.destination !== 'family') {
      continue;
    }

    const familySnapshotResult = await fetchDefinitionOnlyFamilySnapshot({
      parameterNames: operation.parameterNames,
      requestedParameters: buildRequestedParameterIntents([
        {
          parameterNames: operation.parameterNames,
          destination: 'family',
          bindingKind: operation.bindingKind,
        },
      ]),
      onProgress: args.deps.updateParameterProgressFromHost,
    });
    const abort = projectChangeAbort(
      args.requestedProjectKey,
      args.deps.didProjectChange,
      args.notifiers,
    );
    if (abort) {
      return { kind: 'abort', result: abort };
    }

    selections.set(operation, {
      selection: selectConnectorFamilyParameterTargets(
        operation,
        familySnapshotResult.families,
        familySnapshotResult.available,
      ),
      snapshotAvailable: familySnapshotResult.available,
      snapshotFamilyCount: familySnapshotResult.families.length,
      snapshotCategories: collectSnapshotCategories(familySnapshotResult.families),
    });
  }

  return { kind: 'ok', selections };
};

const runProjectConnectorOperation = async (args: {
  operation: ConnectorAdditionOperation;
  requestedProjectKey: string;
  availableBeforeDescriptors: AvailableProjectParameterDescriptors;
  checkpoint: ConnectorUndoCheckpoint;
  operationResults: ParameterAdditionResult[];
  deps: ConnectorAdditionPlanDeps;
  notifiers: ConnectorPlanNotifiers;
}): Promise<ParameterAdditionResult | null> => {
  const { operation, deps, notifiers } = args;
  const mutationBatches = buildTolerantProjectParameterMutationBatches(
    operation.parameterNames,
    operation.bindingKind,
    operation.categories,
    args.availableBeforeDescriptors,
    operation.parameterTypeHints,
  );

  for (const batch of mutationBatches) {
    const result = await deps.addAsProjectParameters(batch.parameterNames, {
      categories: operation.categories,
      bindingKind: batch.bindingKind,
      parameterTypeHints: operation.parameterTypeHints,
    });
    const checkpointError = args.checkpoint.recordProjectResult(result);
    if (checkpointError) {
      notifyParameterAddition(notifiers.options, notifiers.showWarning, checkpointError);
      return { ...result, success: false, error: checkpointError };
    }
    const abort = projectChangeAbort(
      args.requestedProjectKey,
      deps.didProjectChange,
      notifiers,
    );
    if (abort) {
      return abort;
    }
    if (!result.success) {
      notifyParameterAddition(
        notifiers.options,
        notifiers.showWarning,
        result.error || 'Some parameters may not have been added.',
      );
      return result;
    }
    if ((result.failedCount || 0) === 0 && (result.conflictingCount || 0) === 0) {
      rememberAvailableProjectParameters(
        batch.parameterNames,
        batch.bindingKind,
        args.requestedProjectKey,
        operation.categories,
        operation.parameterTypeHints,
      );
    }
    args.operationResults.push(result);
  }

  return null;
};

const runFamilyConnectorOperation = async (args: {
  operation: ConnectorAdditionOperation;
  familySnapshot: ConnectorFamilySnapshotSelection;
  options: ParameterAdditionOptions;
  requestedProjectKey: string;
  pendingOperationRecovery: PendingRevitOperationRecoveryMetadata;
  operationResults: ParameterAdditionResult[];
  checkpoint: ConnectorUndoCheckpoint;
  deps: ConnectorAdditionPlanDeps;
  notifiers: ConnectorPlanNotifiers;
}): Promise<
  | { kind: 'continue' }
  | { kind: 'return'; result: ParameterAdditionResult }
> => {
  const { operation, deps, notifiers } = args;
  const selection = args.familySnapshot.selection;
  const knownFamilyKeys =
    args.options.familyTargetMode === 'newSincePreviousConnectorSetup' &&
      deps.connectorKnownFamilyKeysRef.current?.projectKey === args.requestedProjectKey
      ? deps.connectorKnownFamilyKeysRef.current.familyKeys
      : [];
  const mutationTargetFamilyIds = collectConnectorFamilyMutationTargetIds(
    selection,
    knownFamilyKeys,
  );
  const targetDisposition = resolveConnectorFamilyTargetDisposition({
    selection,
    mutationTargetFamilyIds,
    familyTargetMode: args.options.familyTargetMode,
  });
  recordConnectorFamilyRouteEvidence({
    operation,
    familySnapshot: args.familySnapshot,
    mutationTargetFamilyIds,
    disposition: targetDisposition,
  });
  if (selection.hasReliableSnapshot) {
    deps.connectorKnownFamilyKeysRef.current = {
      projectKey: args.requestedProjectKey,
      familyKeys: new Set(
        selection.matchingFamilies
          .map(buildConnectorFamilyIdentityKey)
          .filter(Boolean),
      ),
    };
  }
  if (targetDisposition === 'alreadySatisfied' || targetDisposition === 'nonApplicable') {
    return { kind: 'continue' };
  }

  const result = await deps.addToConfiguredFamilyCategories(
    operation.parameterNames,
    selection.hasReliableSnapshot ? [] : operation.categories,
    operation.bindingKind,
    selection.hasReliableSnapshot
      ? {
        targetFamilyIds: mutationTargetFamilyIds,
        expectedFamilyCount: mutationTargetFamilyIds.length,
        parameterTypeHints: operation.parameterTypeHints,
        pendingOperationRecovery: args.pendingOperationRecovery,
        requireReversibleMutations: true,
      }
      : {
        parameterTypeHints: operation.parameterTypeHints,
        pendingOperationRecovery: args.pendingOperationRecovery,
        requireReversibleMutations: true,
      },
  );
  const checkpointError = args.checkpoint.recordFamilyResult(result);
  if (checkpointError) {
    notifyParameterAddition(notifiers.options, notifiers.showWarning, checkpointError);
    return {
      kind: 'return',
      result: { ...result, success: false, error: checkpointError },
    };
  }
  const abort = projectChangeAbort(
    args.requestedProjectKey,
    deps.didProjectChange,
    notifiers,
  );
  if (abort) {
    return { kind: 'return', result: abort };
  }
  if (!result.success) {
    notifyParameterAddition(
      notifiers.options,
      notifiers.showWarning,
      result.error || 'Some parameters may not have been added.',
    );
    return { kind: 'return', result };
  }

  args.operationResults.push(result);
  return { kind: 'continue' };
};

const summarizeConnectorOperationResults = (operationResults: ParameterAdditionResult[]) => ({
  familyCount: operationResults.reduce((sum, result) => sum + (result.familyCount || 0), 0),
  hasSystemFamilies: operationResults.some((result) => result.hasSystemFamilies),
  hasLoadableFamilies: operationResults.some((result) => result.hasLoadableFamilies),
  totalAddedCount: operationResults.reduce((sum, result) => sum + (result.addedCount || 0), 0),
  totalUpdatedCount: operationResults.reduce((sum, result) => sum + (result.updatedCount || 0), 0),
  totalExistingCount: operationResults.reduce(
    (sum, result) => sum + (result.existingCount || 0),
    0,
  ),
  totalFailedCount: operationResults.reduce((sum, result) => sum + (result.failedCount || 0), 0),
  totalConflictingCount: operationResults.reduce(
    (sum, result) => sum + (result.conflictingCount || 0),
    0,
  ),
});

const notifyConnectorCompletion = (args: {
  notifiers: ConnectorPlanNotifiers;
  notification: ParameterCompletionNotification;
  undoAvailable: boolean;
  frozenModeDescription: string;
}): void => {
  const { notifiers, notification } = args;
  if (notification.severity === 'warning') {
    notifyParameterAddition(notifiers.options, notifiers.showWarning, notification.message);
    return;
  }
  if (notification.severity === 'info') {
    notifyParameterAddition(notifiers.options, notifiers.showInfo, notification.message);
    return;
  }

  notifyParameterAddition(
    notifiers.options,
    notifiers.showSuccess,
    args.undoAvailable
      ? `${notification.message} ${args.frozenModeDescription}. Undo is available on this step for the parameter setup changes.`
      : `${notification.message} ${args.frozenModeDescription}.`,
  );
};

const finalizeConnectorAddition = (args: {
  operations: ConnectorAdditionOperation[];
  operationResults: ParameterAdditionResult[];
  checkpoint: ConnectorUndoCheckpoint;
  requestedProjectKey: string;
  frozenModeDescription: string;
  deps: ConnectorAdditionPlanDeps;
  notifiers: ConnectorPlanNotifiers;
}): ParameterAdditionResult => {
  const { deps, notifiers } = args;
  const connectorUndoPlan = args.checkpoint.plan;
  const undoAbort = projectChangeAbort(
    args.requestedProjectKey,
    deps.didProjectChange,
    notifiers,
  );
  if (undoAbort) {
    return undoAbort;
  }

  if (args.operationResults.length === 0) {
    notifyParameterAddition(
      notifiers.options,
      notifiers.showInfo,
      `All connector parameters already match the selected mode. ${args.frozenModeDescription}.`,
    );
    return buildAlreadyAvailableResult(args.operations);
  }

  const totals = summarizeConnectorOperationResults(args.operationResults);
  notifyParameterAddition(
    notifiers.options,
    notifiers.showInfo,
    'Connector parameter request completed. Flow cache updated for this project.',
  );
  const notifyAbort = projectChangeAbort(
    args.requestedProjectKey,
    deps.didProjectChange,
    notifiers,
  );
  if (notifyAbort) {
    return notifyAbort;
  }
  notifyConnectorCompletion({
    notifiers,
    notification: buildScopedBindingCompletionNotification({
      requestedCount: countRequestedParameters(args.operations),
      addedOrUpdatedCount: totals.totalAddedCount + totals.totalUpdatedCount,
      existingCount: totals.totalExistingCount,
      conflictingCount: totals.totalConflictingCount,
      failedCount: totals.totalFailedCount,
    }),
    undoAvailable: connectorUndoPlan !== null,
    frozenModeDescription: args.frozenModeDescription,
  });

  return {
    success: totals.totalFailedCount === 0 && totals.totalConflictingCount === 0,
    addedCount: totals.totalAddedCount,
    updatedCount: totals.totalUpdatedCount,
    existingCount: totals.totalExistingCount,
    failedCount: totals.totalFailedCount,
    conflictingCount: totals.totalConflictingCount,
    familyCount: totals.familyCount,
    hasSystemFamilies: totals.hasSystemFamilies,
    hasLoadableFamilies: totals.hasLoadableFamilies,
    queued: args.operationResults.some((result) => result.queued),
    requestedCount: countRequestedParameters(args.operations),
    undoAvailable: connectorUndoPlan !== null,
  };
};

const runConnectorPlanStages = async (args: {
  operations: ConnectorAdditionOperation[];
  requestedProjectKey: string;
  frozenModeDescription: string;
  pendingOperationRecovery: PendingRevitOperationRecoveryMetadata;
  options: ParameterAdditionOptions;
  deps: ConnectorAdditionPlanDeps;
  notifiers: ConnectorPlanNotifiers;
}): Promise<ParameterAdditionResult> => {
  const { operations, requestedProjectKey, deps, notifiers } = args;
  const availableBeforeDescriptors = await fetchAvailableProjectParameters({
    cacheScopeKey: requestedProjectKey,
    force: true,
  });
  const fetchAbort = projectChangeAbort(requestedProjectKey, deps.didProjectChange, notifiers);
  if (fetchAbort) {
    return fetchAbort;
  }

  if (areConnectorAdditionOperationsAvailable(operations, availableBeforeDescriptors)) {
    notifyParameterAddition(
      args.options,
      deps.showInfo,
      `All connector parameters already match the selected mode. ${args.frozenModeDescription}.`,
    );
    return buildAlreadyAvailableResult(operations);
  }

  const prepass = await resolveFamilySelections({
    operations,
    requestedProjectKey,
    deps,
    notifiers,
  });
  if (prepass.kind === 'abort') {
    return prepass.result;
  }

  const operationResults: ParameterAdditionResult[] = [];
  const checkpoint = createConnectorUndoCheckpoint(
    requestedProjectKey,
    deps.setConnectorParameterUndoPlan,
  );
  for (const operation of operations) {
    if (operation.destination === 'project') {
      const bubbled = await runProjectConnectorOperation({
        operation,
        requestedProjectKey,
        availableBeforeDescriptors,
        checkpoint,
        operationResults,
        deps,
        notifiers,
      });
      if (bubbled) {
        return bubbled;
      }
      continue;
    }

    const outcome = await runFamilyConnectorOperation({
      operation,
      familySnapshot: prepass.selections.get(operation)!,
      options: args.options,
      requestedProjectKey,
      pendingOperationRecovery: args.pendingOperationRecovery,
      operationResults,
      checkpoint,
      deps,
      notifiers,
    });
    if (outcome.kind === 'return') {
      return outcome.result;
    }
  }

  return finalizeConnectorAddition({
    operations,
    operationResults,
    checkpoint,
    requestedProjectKey,
    frozenModeDescription: args.frozenModeDescription,
    deps,
    notifiers,
  });
};

export async function executeConnectorAdditionPlan(
  options: ParameterAdditionOptions,
  frozenContext: ConnectorExecutionContext,
  deps: ConnectorAdditionPlanDeps,
): Promise<ParameterAdditionResult> {
  const frozenModeDescription = describeConnectorExecutionMode(
    frozenContext.destination,
    frozenContext.bindingKind,
  );
  const notifiers: ConnectorPlanNotifiers = {
    options,
    showInfo: deps.showInfo,
    showSuccess: deps.showSuccess,
    showWarning: deps.showWarning,
  };
  const operations = buildConnectorAdditionOperations({
    menuConfig: deps.menuConfig,
    connectorConfig: withConnectorAdditionTarget(deps.connectorConfig, {
      parameterDestination: frozenContext.destination,
      parameterBindingKind: frozenContext.bindingKind,
    }),
  });
  if (operations.length === 0) {
    notifyParameterAddition(options, deps.showWarning, 'No parameters configured to add');
    return { success: true, addedCount: 0 };
  }

  const requestedProjectKey = frozenContext.projectKey;
  if (!requestedProjectKey) {
    const error =
      'Active Revit project identity is not available yet. Recheck this step before continuing.';
    notifyParameterAddition(options, deps.showWarning, error);
    return { success: false, addedCount: 0, error };
  }

  deps.setIsAdding(true);
  deps.startParameterProgress({
    title: 'Adding connector parameters',
    message: 'Revit is applying connector parameter setup',
    detail: frozenModeDescription,
  });
  deps.setConnectorParameterUndoPlan(null);

  // Provenance for the family mutation's pending record: the connector caller
  // owns the record identity; the generic family client only appends the
  // mechanical operation fields once the backend returns an operation id.
  const pendingOperationRecovery: PendingRevitOperationRecoveryMetadata = {
    owner: frozenContext.owner,
    title: 'Adding connector parameters',
    projectKey: frozenContext.projectKey,
    expectedDocumentName: frozenContext.expectedDocumentName,
    expectedDocumentPath: frozenContext.expectedDocumentPath,
    connectorTarget: {
      destination: frozenContext.destination,
      bindingKind: frozenContext.bindingKind,
    },
  };

  try {
    return await runConnectorPlanStages({
      operations,
      requestedProjectKey,
      frozenModeDescription,
      pendingOperationRecovery,
      options,
      deps,
      notifiers,
    });
  } catch (error) {
    console.error('[useParameterAddition] Error:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    notifyParameterAddition(options, deps.showError, `Failed to add parameters: ${errorMessage}`);
    return { success: false, addedCount: 0, error: errorMessage };
  } finally {
    deps.setIsAdding(false);
  }
}
